import React, { useCallback, useEffect, useState } from "react";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { useAxios } from "@/hooks/use-axios/useAxios";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import type { NewInspection } from "../InspectionCreator.d";
import InputText from "@/components/input-text/InputText";
import InputCalendar from "@/components/input-calendar/InputCalendar";
import InputCheckbox from "@/components/input-checkbox/InputCheckbox";
import TerciaryButton from "@/components/buttons/terciary-button/TerciaryButton";
import routes from "@/assets/routes/routes";
import { useNavigate } from "react-router-dom";
import { BASE_URL, STATUS } from "@/assets/constants/constants";
import useInspectionId from "@/store/useInspectionId";

interface NewInspectionFormProps {
  newInspectionData: NewInspection;
  getError: (errorMsg: string) => void;
  close: () => void;
}

const NewInspectionForm: React.FC<NewInspectionFormProps> = ({
  newInspectionData,
  getError,
  close,
}) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { request, buildRoute } = useAxios();
  const { updateIsNewInspectionId, updateSnStand } = useInspectionId();
  const [isCsPmo, setIsCsPmo] = useState(false);
  const [project, setProject] = useState<string>("");

  useEffect(() => {
    if (
      newInspectionData.project?.length &&
      newInspectionData.project.length > 0
    ) {
      setProject(newInspectionData.project);
    }
  }, [newInspectionData.project]);

  const changeIsCsPmo = useCallback(() => {
    setIsCsPmo((prevState) => !prevState);
  }, []);

  const changeProject = useCallback((data: string) => {
    setProject(data);
  }, []);

  const createInspection = useCallback(async () => {
    getError("");
    try {
      const bodyData = {
        project: project && project.trim().length > 0 ? project.trim() : "",
        isCsPmo: isCsPmo,
        status: STATUS.IN_PROGRESS,
      };

      const url = buildRoute(routes.updateInpectionId, {
        id: newInspectionData.id,
      });
      const newInspection = await request({
        url: url,
        method: "PATCH",
        body: bodyData,
      });
      if (newInspection?.id) {
        updateSnStand(newInspectionData.nsStand);
        updateIsNewInspectionId(true);
        navigate(`${BASE_URL}/${newInspection.id}`);
      }
    } catch {
      getError("noPossibleCreateInspection");
    }
  }, [
    isCsPmo,
    newInspectionData,
    project,
    buildRoute,
    getError,
    navigate,
    request,
    updateIsNewInspectionId,
    updateSnStand,
  ]);

  return (
    <div className="w-[714px] max-w-[calc(100vw-48px)]">
      <div className="grid grid-cols-2 max-[767px]:grid-cols-1 gap-6 mb-4">
        <div>
          <InputText
            size="medium"
            noBottomSpace
            readOnly
            value={newInspectionData.nsStand}
            label={translate("snStand")}
            placeholder=""
          />
        </div>
        <div>
          <InputCalendar
            size="medium"
            name="date"
            noBottomSpace
            readOnly
            label={translate("startDate")}
            value={newInspectionData.startDate as Date}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 max-[767px]:grid-cols-1 gap-6 mb-4">
        <div>
          <InputText
            size="medium"
            noBottomSpace
            readOnly
            value={newInspectionData.engineName as string}
            label={translate("engineType")}
            placeholder=""
          />
        </div>
        <div>
          <InputText
            size="medium"
            noBottomSpace
            value={project}
            label={translate("project")}
            placeholder=""
            onChange={changeProject}
          />
        </div>
      </div>

      <div className="w-[calc(50%-24px)] mb-4">
        <InputText
          size="medium"
          noBottomSpace
          readOnly
          value={newInspectionData.esnIn as string}
          label={translate("esnEntry")}
          placeholder=""
        />
      </div>

      <div className="font-[Verdana] font-ib-bold text-[13px] leading-[20px] text-ib-gray-darkest pt-4 mb-4 w-full border-t border-t-ib-gray-borders">
        {translate("preChecks")}
      </div>
      <div className="grid grid-cols-2 max-[767px]:grid-cols-1 gap-6 mb-4">
        <div>
          <InputText
            size="medium"
            noBottomSpace
            readOnly
            value={newInspectionData.nsCradle as string}
            label={translate("cradleNumber")}
            placeholder=""
          />
        </div>
        <div>
          <InputText
            size="medium"
            noBottomSpace
            readOnly
            value={newInspectionData.owner as string}
            label={translate("owner")}
            placeholder=""
          />
        </div>
      </div>

      <div className="w-[calc(50%-24px)] mb-4">
        <InputText
          size="medium"
          noBottomSpace
          readOnly
          value={newInspectionData.colour as string}
          label={translate("colour")}
          placeholder=""
        />
      </div>

      <div className="mb-4 h-6 w-full ">
        <InputCheckbox
          id="pmo-check"
          title={translate("carIndicated")}
          onChange={changeIsCsPmo}
        />
      </div>

      <div className="w-full pt-4 border-t border-t-ib-gray-borders flex flex-row justify-between items-center">
        <div className="w-fit">
          <TerciaryButton
            label={translate("cancel")}
            onClick={close}
            colour="red"
          />
        </div>

        <div className="w-fit">
          <PrimaryButton
            label={translate("createNewInspection")}
            colour="red"
            size="large"
            onClick={createInspection}
          />
        </div>
      </div>
    </div>
  );
};

export default NewInspectionForm;
